import React, { useState, useRef, useEffect } from 'react';
import { Form, Button, InputGroup } from 'react-bootstrap';
import { Wallet, Edit2, Check, X } from 'lucide-react';
import { ProjectModel, BudgetAllocation } from '../../../types/projects';
import { formatCurrency } from '../../../Helpers/FormatUtils';
import { sumAllocation, parseAllocationInput } from '../../../Helpers/budgetAllocation';

interface ProjectBudgetSectionProps {
    project: ProjectModel;
    onUpdateBudgetAllocation: (allocation: BudgetAllocation | null) => Promise<void>;
    submitting?: boolean;
}

// The three budget buckets, in display order.
const BUCKETS: { key: keyof BudgetAllocation; label: string }[] = [
    { key: 'investmentAmount', label: 'Investering' },
    { key: 'buildingMaintenanceAmount', label: 'Underhåll byggnad' },
    { key: 'propertyTechMaintenanceAmount', label: 'Underhåll fastighetsteknik' },
];

type FieldValues = Record<keyof BudgetAllocation, string>;

const EMPTY_FIELDS: FieldValues = {
    investmentAmount: '',
    buildingMaintenanceAmount: '',
    propertyTechMaintenanceAmount: ''
};

const fieldsFromBudget = (budget?: BudgetAllocation): FieldValues => ({
    investmentAmount: budget?.investmentAmount ? String(budget.investmentAmount) : '',
    buildingMaintenanceAmount: budget?.buildingMaintenanceAmount ? String(budget.buildingMaintenanceAmount) : '',
    propertyTechMaintenanceAmount: budget?.propertyTechMaintenanceAmount ? String(budget.propertyTechMaintenanceAmount) : ''
});

export const ProjectBudgetSection: React.FC<ProjectBudgetSectionProps> = ({
    project,
    onUpdateBudgetAllocation,
    submitting = false,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [fields, setFields] = useState<FieldValues>(EMPTY_FIELDS);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const firstInputRef = useRef<HTMLInputElement>(null);

    // Focus the first input when entering edit mode
    useEffect(() => {
        if (isEditing && firstInputRef.current) {
            firstInputRef.current.focus();
            firstInputRef.current.select();
        }
    }, [isEditing]);

    const handleEdit = () => {
        setFields(fieldsFromBudget(project.budget));
        setError(null);
        setIsEditing(true);
    };

    const handleSave = async () => {
        const result = parseAllocationInput(
            fields.investmentAmount,
            fields.buildingMaintenanceAmount,
            fields.propertyTechMaintenanceAmount
        );
        if (!result.ok) {
            setError(result.error);
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await onUpdateBudgetAllocation(result.allocation);
            setIsEditing(false);
        } catch (err) {
            console.error('Error saving budget:', err);
            setError('Kunde inte spara budget');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFields(EMPTY_FIELDS);
        setError(null);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    const setField = (key: keyof BudgetAllocation, value: string) => {
        setFields(prev => ({ ...prev, [key]: value }));
    };

    // Live total of the current edit inputs (blank/invalid → 0 for display only).
    const editingTotal = BUCKETS.reduce((sum, { key }) => {
        const value = Number(fields[key]);
        return sum + (Number.isFinite(value) && value > 0 ? value : 0);
    }, 0);

    const hasBudget = project.budget != null;

    const labelStyle: React.CSSProperties = {
        display: 'flex', alignItems: 'center', gap: '6px',
        color: '#000', fontSize: '14px', fontWeight: 700, marginBottom: '1px',
    };
    const valueIndent: React.CSSProperties = { paddingLeft: '20px' };

    // Don't render the breakdown if no budget set and not editing
    if (!hasBudget && !isEditing) {
        return (
            <div style={{ marginBottom: '24px' }}>
                <div style={labelStyle}>
                    <Wallet size={14} />
                    Budget:
                </div>
                <div style={{ ...valueIndent, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px', color: '#999', fontStyle: 'italic' }}>Ej angiven</span>
                    {!submitting && (
                        <Button size="sm" variant="link" onClick={handleEdit} style={{ padding: '2px 6px', color: '#666' }}>
                            <Edit2 size={14} />
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    if (isEditing) {
        return (
            <div style={{ marginBottom: '24px' }}>
                <div style={labelStyle}>
                    <Wallet size={14} />
                    Budget:
                </div>
                <div style={{ ...valueIndent, marginTop: '4px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '320px' }}>
                        {BUCKETS.map(({ key, label }, index) => (
                            <Form.Group key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Form.Label style={{ fontSize: '13px', color: '#666', margin: 0, flex: 1 }}>
                                    {label}
                                </Form.Label>
                                <InputGroup size="sm" style={{ width: '150px' }}>
                                    <Form.Control
                                        ref={index === 0 ? firstInputRef : undefined}
                                        type="number"
                                        value={fields[key]}
                                        onChange={(e) => setField(key, e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="0"
                                        min="0"
                                        step="1000"
                                        disabled={saving || submitting}
                                    />
                                    <InputGroup.Text>kr</InputGroup.Text>
                                </InputGroup>
                            </Form.Group>
                        ))}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderTop: '1px solid #e9ecef', paddingTop: '8px' }}>
                            <span style={{ fontSize: '13px', color: '#666', fontWeight: 600, flex: 1 }}>Budget totalt</span>
                            <span style={{ width: '150px', fontSize: '14px', fontWeight: 600 }}>{formatCurrency(editingTotal)}</span>
                        </div>
                    </div>
                    {error && (
                        <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '6px' }}>{error}</div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                        <Button size="sm" variant="success" onClick={handleSave} disabled={saving || submitting}>
                            <Check size={16} />
                        </Button>
                        <Button size="sm" variant="secondary" onClick={handleCancel} disabled={saving || submitting}>
                            <X size={16} />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Display mode
    const total = sumAllocation(project.budget);
    return (
        <div style={{ marginBottom: '24px' }}>
            <div style={labelStyle}>
                <Wallet size={14} />
                Budget:
            </div>
            <div style={valueIndent}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px', fontWeight: 600 }}>{formatCurrency(total)}</span>
                    {!submitting && (
                        <Button size="sm" variant="link" onClick={handleEdit} style={{ padding: '2px 6px', color: '#666' }}>
                            <Edit2 size={14} />
                        </Button>
                    )}
                </div>
                <div style={{ marginTop: '2px' }}>
                    {BUCKETS.filter(({ key }) => (project.budget?.[key] ?? 0) > 0).map(({ key, label }) => (
                        <div key={key} style={{ fontSize: '14px', color: '#666' }}>
                            {label}: {formatCurrency(project.budget![key])}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
